import React from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { EmailBlock, EmailBlockType } from './types';
import { emailBuilderBlockBackground } from './blockStyles';
import { RowActions } from './RowActions';

export const ButtonBlock: React.FC<{
  block: EmailBlock;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<EmailBlock>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}> = ({ block, isSelected, onSelect, onDelete, onDuplicate, onDragStart, onDragEnd }) => {
  const common = {
    px: 2,
    py: 1.5,
    border: '1px solid',
    borderRadius: 2,
    mx: 1,
    borderColor: isSelected ? 'primary.main' : 'divider',
    backgroundColor: emailBuilderBlockBackground,
    cursor: 'move',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
      backgroundColor: 'action.hover',
      borderColor: isSelected ? 'primary.main' : 'primary.light'
    }
  } as const;
  return (
    <Box sx={common} draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onSelect}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ mb: 0, wordBreak: 'break-word' }}>
          Button : {block.title || 'Button'}
        </Typography>
        <RowActions onDelete={onDelete} onDuplicate={onDuplicate} />
      </Box>
    </Box>
  );
};

export const ButtonBlockDialog: React.FC<{
  open: boolean;
  draft: EmailBlock;
  setDraft: (next: EmailBlock) => void;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, draft, setDraft, onCancel, onConfirm }) => {
  if (draft.type !== 'button') return null;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Button</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title"
            value={draft.title || ''}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            fullWidth
            size="small"
          />

          <TextField
            label="Button Label"
            value={draft.content || ''}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            fullWidth
            size="small"
            placeholder="Click me"
          />

          <TextField
            label="URL"
            value={draft.url || ''}
            onChange={(e) => setDraft({ ...draft, url: e.target.value })}
            fullWidth
            size="small"
            placeholder="https://example.com"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Align</InputLabel>
            <Select
              value={draft.align || 'center'}
              onChange={(e) => setDraft({ ...draft, align: e.target.value as any })}
              label="Align"
            >
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="center">Center</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Size</InputLabel>
            <Select
              value={draft.size || 'medium'}
              onChange={(e) => setDraft({ ...draft, size: e.target.value as any })}
              label="Size"
            >
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Background Color"
              type="color"
              value={draft.backgroundColor || '#0d6efd'}
              onChange={(e) => setDraft({ ...draft, backgroundColor: e.target.value })}
              fullWidth
              size="small"
              InputProps={{
                style: { height: '40px' }
              }}
            />

            <TextField
              label="Text Color"
              type="color"
              value={draft.textColor || '#ffffff'}
              onChange={(e) => setDraft({ ...draft, textColor: e.target.value })}
              fullWidth
              size="small"
              InputProps={{
                style: { height: '40px' }
              }}
            />
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Target</InputLabel>
            <Select
              value={draft.target || '_self'}
              onChange={(e) => setDraft({ ...draft, target: e.target.value as any })}
              label="Target"
            >
              <MenuItem value="_self">Same Window</MenuItem>
              <MenuItem value="_blank">New Tab</MenuItem>
            </Select>
          </FormControl>

          {/* Button Preview */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>Preview:</Typography>
            <Button
              variant="contained"
              size={draft.size || 'medium'}
              sx={{
                backgroundColor: draft.backgroundColor || '#0d6efd',
                color: draft.textColor || '#ffffff',
                '&:hover': {
                  backgroundColor: draft.backgroundColor || '#0d6efd',
                }
              }}
            >
              {draft.content || 'Button Label'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancel}>Cancel</Button>
        <Button color="success" variant="contained" onClick={onConfirm}>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

// Button block instantiation function
export const instantiateButtonBlock = (): EmailBlock => {
  return {
    id: '',
    type: 'button',
    title: 'Button',
    content: 'Click me',
    url: '#',
    align: 'center',
    size: 'medium',
    backgroundColor: '#0d6efd',
    textColor: '#ffffff',
    target: '_self'
  };
};

// Button block HTML rendering function
export const renderButtonBlockToHtml = (block: EmailBlock): string => {
  const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  
  const align = block.align || 'center';
  const size = block.size || 'medium';
  const padding = size === 'small' ? '8px 14px' : size === 'large' ? '14px 22px' : '10px 18px';
  const backgroundColor = block.backgroundColor || '#0d6efd';
  const textColor = block.textColor || '#ffffff';
  const target = block.target || '_self';
  
  return `<tr><td style="padding:16px 24px; text-align:${align}"><a href="${escapeHtml(block.url || '#')}" target="${target}" style="display:inline-block; background:${backgroundColor}; color:${textColor}; text-decoration:none; border-radius:6px; padding:${padding}; font-family:Arial, Helvetica, sans-serif;">${escapeHtml(block.content || 'Click me')}</a></td></tr>`;
};


